import React from 'react';
import { TabState, DatasetId, ReportId, ModelId, TabId, createTabId, EphemeralDataSet } from '../../types';
import {
  DataTableTabContent,
  StatisticsTabContent,
  ReportTabContent,
  HelpTabContent,
  GLMTabContent,
  GLMDiagnosticsTabContent,
  GLMPredictionTabContent,
  ProjectOverviewTabContent,
  ModelDetailTabContent,
  SQLEditorTabContent,
  SQLQueryViewerTabContent,
  SelectedRowsTabContent,
  ProjectLineageTabContent,
  CrosstabTabContent,
  ExcludedRowsTabContent,
  FilteredDataTabContent,
  ColumnTypeConversionTabContent,
  DummyCodingTabContent,
  ProjectDiffTabContent
} from '../components/TabContainer/tabs';
import { GraphBuilderTabContent } from '../components/graph-builder';

/**
 * TabRegistry
 * 
 * タブタイプごとの設定を一元管理するレジストリ
 * - タブの作成関数
 * - 対応するコンポーネント
 * - 必要なContext
 * - 永続化設定
 */

export interface TabRegistryEntry {
  // タブ作成関数
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createTab: (...args: any[]) => TabState;
  
  // タブコンポーネント
  component: React.ComponentType<{ tab: TabState }>;
  
  // タブが永続化（keepMounted）されるべきか
  persistDOM?: boolean;
  
  // このタブが必要とするContext（将来の拡張用）
  requiredContexts?: string[];
  
  // タブが単一インスタンスか（同じタイプを複数開けるか）
  singleton?: boolean;
  
  // タブのアイコン
  icon?: string;
  
  // タブの説明
  description?: string;
  
  // コマンドパレットに表示するラベル
  label?: string;
  
  // コマンドパレットに表示するかどうか
  showInCommandPalette?: boolean;
}

// タブID生成のヘルパー関数
const generateTabId = (type: string, suffix?: string): TabId => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 9);
  const id = suffix ? `${type}-${suffix}_${timestamp}_${random}` : `${type}-${timestamp}_${random}`;
  return createTabId(id);
};

// タブ作成関数
export const createDataTableTab = (datasetId: DatasetId, title: string): TabState => ({
  id: generateTabId('data-table', datasetId),
  title,
  type: 'data-table',
  isClosable: true,
  metadata: { datasetId }
});

export const createStatisticsTab = (): TabState => ({
  id: generateTabId('statistics'),
  title: 'Statistics',
  type: 'statistics',
  isClosable: true
});

export const createReportTab = (reportId: ReportId, title: string): TabState => ({
  id: generateTabId('report', reportId),
  title,
  type: 'report',
  isClosable: true,
  metadata: { reportId }
});

export const createSQLEditorTab = (): TabState => ({
  id: generateTabId('sql-editor'),
  title: 'SQL Query Editor',
  type: 'sql-editor',
  isClosable: true,
  keepMounted: true
});

export const createSQLQueryViewerTab = (datasetId: DatasetId, title: string): TabState => ({
  id: generateTabId('sql-query-viewer', datasetId),
  title,
  type: 'sql-query-viewer',
  isClosable: true,
  metadata: { datasetId }
});

export const createHelpTab = (): TabState => ({
  id: generateTabId('help'),
  title: 'Help',
  type: 'help',
  isClosable: true
});

export const createGLMTab = (): TabState => ({
  id: generateTabId('glm'),
  title: 'GLM',
  type: 'glm',
  isClosable: true,
  keepMounted: true
});

export const createProjectOverviewTab = (): TabState => ({
  id: generateTabId('project-overview'),
  title: 'Project Overview',
  type: 'project-overview',
  isClosable: true
});

export const createModelDetailTab = (modelId: ModelId, title: string): TabState => ({
  id: generateTabId('model-detail', modelId),
  title,
  type: 'model-detail',
  isClosable: true,
  metadata: { modelId }
});

export const createSelectedRowsTab = (datasetId?: DatasetId): TabState => ({
  id: generateTabId('selected-rows'),
  title: 'Selected Rows',
  type: 'selected-rows',
  isClosable: true,
  metadata: datasetId ? { datasetId } : undefined
});

export const createProjectLineageTab = (): TabState => ({
  id: generateTabId('project-lineage'),
  title: 'Project Lineage',
  type: 'project-lineage',
  isClosable: true
});

export const createCrosstabTab = (): TabState => ({
  id: generateTabId('crosstab'),
  title: 'Crosstab',
  type: 'crosstab',
  isClosable: true,
  keepMounted: true
});

export const createGraphBuilderTab = (initialGraphType?: string): TabState => ({
  id: generateTabId('graph-builder'),
  title: 'Graph Builder',
  type: 'graph-builder',
  isClosable: true,
  keepMounted: true,
  tabViewState: initialGraphType ? {
    selectedGraphType: initialGraphType,
    selectedDatasetId: undefined,
    currentConfig: undefined,
    validationError: undefined
  } : undefined
});

export const createExcludedRowsTab = (): TabState => ({
  id: generateTabId('excluded-rows'),
  title: 'Excluded Rows',
  type: 'excluded-rows',
  isClosable: true
});

export const createFilteredDataTab = (
  datasetId: DatasetId,
  title: string,
  ephemeralDataset: EphemeralDataSet
): TabState => ({
  id: generateTabId('filtered-data', datasetId),
  title,
  type: 'filtered-data',
  isClosable: true,
  metadata: { datasetId, ephemeralDataset }
});

export const createColumnTypeConversionTab = (
  datasetId?: DatasetId,
  columnId?: string
): TabState => ({
  id: generateTabId('column-type-conversion', datasetId ?? 'new'),
  title: 'Convert Column Types',
  type: 'column-type-conversion',
  isClosable: true,
  keepMounted: true,  // ドロップダウン状態保持のため
  metadata: { datasetId, columnId }
});

export const createDummyCodingTab = (datasetId?: DatasetId): TabState => ({
  id: generateTabId('dummy-coding', datasetId ?? 'new'),
  title: 'Dummy Coding',
  type: 'dummy-coding',
  isClosable: true,
  keepMounted: true,  // ドロップダウン状態保持のため
  metadata: { datasetId }
});

export const createProjectDiffTab = (): TabState => ({
  id: generateTabId('project-diff'),
  title: 'Compare Project',
  type: 'project-diff',
  isClosable: true
});

// TabRegistry本体
export const TabRegistry: Record<TabState['type'], TabRegistryEntry> = {
  'data-table': {
    createTab: createDataTableTab,
    component: DataTableTabContent,
    persistDOM: true,  // タブ切り替え時もDOMを維持（ソート状態保持のため）
    requiredContexts: ['ProjectDataContext'],
    description: 'View and explore dataset contents',
    showInCommandPalette: false // データセット固有のタブなので表示しない
  },
  
  'statistics': {
    createTab: createStatisticsTab,
    component: StatisticsTabContent,
    singleton: true,
    requiredContexts: ['ProjectDataContext'],
    description: 'Statistical analysis',
    label: 'Statistics',
    icon: '📊',
    showInCommandPalette: true
  },
  
  'report': {
    createTab: createReportTab,
    component: ReportTabContent,
    requiredContexts: ['ProjectDataContext'],
    description: 'Create and view analysis reports',
    showInCommandPalette: false // レポート固有のタブなので表示しない
  },
  
  'sql-editor': {
    createTab: createSQLEditorTab,
    component: SQLEditorTabContent,
    persistDOM: true,
    singleton: true,
    requiredContexts: ['ProjectDataContext'],
    description: 'Query data using SQL',
    label: 'SQL Query Editor',
    icon: '🔍',
    showInCommandPalette: true
  },
  
  'sql-query-viewer': {
    createTab: createSQLQueryViewerTab,
    component: SQLQueryViewerTabContent,
    requiredContexts: ['ProjectDataContext'],
    description: 'View SQL query for derived dataset',
    label: 'SQL Query Viewer',
    icon: '📜',
    showInCommandPalette: false // ユーザーから直接作成されることはない
  },
  
  'help': {
    createTab: createHelpTab,
    component: HelpTabContent,
    singleton: true,
    description: 'View help documentation',
    label: 'Help',
    icon: '❓',
    showInCommandPalette: true
  },

  'glm': {
    createTab: createGLMTab,
    component: GLMTabContent,
    persistDOM: true,
    singleton: true,
    requiredContexts: ['ProjectDataContext'],
    description: 'Generalized Linear Model (GLM) analysis',
    label: 'GLM',
    icon: '📊',
    showInCommandPalette: true
  },

  'glm-diagnostics': {
    createTab: (modelId: ModelId, title: string = 'GLM Diagnostics') => ({
      id: generateTabId('glm-diagnostics', modelId),
      title,
      type: 'glm-diagnostics' as const,
      isClosable: true,
      metadata: { modelId }
    }),
    component: GLMDiagnosticsTabContent,
    requiredContexts: ['ProjectDataContext'],
    description: 'Diagnostic plots for GLM analysis',
    showInCommandPalette: false // GLMタブから自動的に開かれるタブ
  },

  'glm-prediction': {
    createTab: (modelId: ModelId, title: string = 'GLM Prediction') => ({
      id: generateTabId('glm-prediction', modelId),
      title,
      type: 'glm-prediction' as const,
      isClosable: true,
      keepMounted: true,
      metadata: { modelId }
    }),
    component: GLMPredictionTabContent,
    persistDOM: true,
    requiredContexts: ['ProjectDataContext'],
    description: 'Make predictions using trained GLM model',
    showInCommandPalette: false // Model Detailタブから開かれるタブ
  },

  'project-overview': {
    createTab: createProjectOverviewTab,
    component: ProjectOverviewTabContent,
    singleton: true,
    requiredContexts: ['ProjectDataContext'],
    description: 'View all datasets, reports, and models',
    label: 'Project Overview',
    icon: '📁',
    showInCommandPalette: true
  },
  
  'model-detail': {
    createTab: createModelDetailTab,
    component: ModelDetailTabContent,
    requiredContexts: ['ProjectDataContext'],
    description: 'View model details',
    showInCommandPalette: false // モデル固有のタブなので表示しない
  },
  
  'selected-rows': {
    createTab: createSelectedRowsTab,
    component: SelectedRowsTabContent,
    requiredContexts: ['ProjectDataContext'],
    description: 'View currently selected rows as a table',
    label: 'Selected Rows',
    icon: '📋',
    showInCommandPalette: true
  },

  'project-lineage': {
    createTab: createProjectLineageTab,
    component: ProjectLineageTabContent,
    singleton: true,
    requiredContexts: ['ProjectDataContext'],
    description: 'Visualize project data dependencies',
    label: 'Project Lineage',
    icon: '🌳',
    showInCommandPalette: true
  },
  
  'crosstab': {
    createTab: createCrosstabTab,
    component: CrosstabTabContent,
    persistDOM: true,
    requiredContexts: ['ProjectDataContext'],
    description: 'Create pivot tables and crosstabs',
    label: 'Crosstab',
    icon: '⊞',
    showInCommandPalette: true
  },
  
  'graph-builder': {
    createTab: createGraphBuilderTab,
    component: GraphBuilderTabContent,
    persistDOM: true,
    requiredContexts: ['ProjectDataContext'],
    description: 'Create graphs and visualizations with GUI',
    label: 'Graph Builder',
    icon: '📊',
    showInCommandPalette: true
  },
  
  'excluded-rows': {
    createTab: createExcludedRowsTab,
    component: ExcludedRowsTabContent,
    singleton: true,
    requiredContexts: ['ProjectDataContext'],
    description: 'Manage excluded rows from datasets',
    label: 'Excluded Rows',
    icon: '🚫',
    showInCommandPalette: true
  },

  'filtered-data': {
    createTab: createFilteredDataTab,
    component: FilteredDataTabContent,
    requiredContexts: ['ProjectDataContext'],
    description: 'View and save filtered data',
    label: 'Filtered Data',
    icon: '🔍',
    showInCommandPalette: false  // クロス集計から自動的に開かれるタブ
  },

  'column-type-conversion': {
    createTab: createColumnTypeConversionTab,
    component: ColumnTypeConversionTabContent,
    requiredContexts: ['ProjectDataContext'],
    description: 'Convert column data types with preview',
    label: 'Convert Column Types',
    icon: '🔄',
    showInCommandPalette: true  // メニューとコマンドパレットから開ける
  },

  'dummy-coding': {
    createTab: createDummyCodingTab,
    component: DummyCodingTabContent,
    requiredContexts: ['ProjectDataContext'],
    description: 'Create dummy variables from categorical columns',
    label: 'Dummy Coding',
    icon: '🔢',
    showInCommandPalette: true  // メニューとコマンドパレットから開ける
  },

  'project-diff': {
    createTab: createProjectDiffTab,
    component: ProjectDiffTabContent,
    singleton: true,
    requiredContexts: ['ProjectDataContext'],
    description: 'Compare current project with another MDS file',
    label: 'Compare Project',
    icon: '🔄',
    showInCommandPalette: true
  }
};

// タブタイプの検証
export function isValidTabType(type: string): type is TabState['type'] {
  return type in TabRegistry;
}

// シングルトンタブかどうかの確認
export function isSingletonTab(type: TabState['type']): boolean {
  return TabRegistry[type]?.singleton === true;
}

// DOM永続化が必要かどうかの確認
export function shouldPersistDOM(type: TabState['type']): boolean {
  return TabRegistry[type]?.persistDOM === true;
}

// タブ作成オプション用の型
export interface TabCreationOption {
  id: string;
  label: string;
  icon: string;
  description: string;
  action: () => TabState;
}

// コマンドパレット用のタブ作成オプションを生成
export function getCommandPaletteTabOptions(): TabCreationOption[] {
  return Object.entries(TabRegistry)
    .filter(([_, entry]) => entry.showInCommandPalette)
    .map(([type, entry]) => ({
      id: type,
      label: entry.label || type,
      icon: entry.icon || '📄',
      description: entry.description || '',
      action: () => entry.createTab()
    }));
}